import type { AxiosRequestConfig } from 'axios';

import buildQuery from 'odata-query';
import { type QueryOptions } from 'odata-query';
import {
  GetMappingAuthorization,
  PostMappingAuthorization,
  PatchMappingAuthorization,
  DeleteMappingAuthorization,
  PutMappingAuthorization,
} from './axiosInstance';
import type { BaseEntity } from '@/models';
import { ODATA_CONTROLLER_NAME, ODATA_SERVER_NAME } from '@/models/odata/constant';

export type PrefixType = 'public' | 'allow' | 'auth';

// Định nghĩa kiểu dữ liệu cho response từ API
export interface ApiResponse<T> {
  success?: boolean;
  data?: T;
  error?: string;
  count?: number;
  value?: T;
}

interface PropsAxios<T = any> {
  url?: string;
  config?: AxiosRequestConfig & T;
  body?: any;
}

export class BaseService<T extends BaseEntity<TKey>, TKey extends string | number> {
  protected endpoint: string;
  protected entityType: any;
  protected prefix: PrefixType;
  protected suffix: string;

  constructor(entityType: any, prefix: PrefixType = 'public', suffix: string = '') {
    this.entityType = entityType;
    this.prefix = prefix;
    this.suffix = suffix;

    // Lấy metadata của đối tượng
    const serverName = Reflect.getMetadata(ODATA_SERVER_NAME, entityType);
    const controllerName = Reflect.getMetadata(ODATA_CONTROLLER_NAME, entityType);

    // Tạo endpoint với suffix
    if (serverName) {
      this.endpoint = `/${serverName}/${this.prefix}/${controllerName}${this.suffix}`;
    } else {
      this.endpoint = `/${this.prefix}/${controllerName}${this.suffix}`;
    }
  }

  protected removeSecondQuestionMark(str: string) {
    // Tìm vị trí của ký tự "?" đầu tiên
    let firstQuestionMarkIndex = str.indexOf('?');

    // Nếu không tìm thấy ký tự "?" nào, trả về chuỗi gốc
    if (firstQuestionMarkIndex === -1) return str;

    // Tìm vị trí của ký tự "?" thứ hai bắt đầu từ vị trí sau ký tự "?" đầu tiên
    let secondQuestionMarkIndex = str.indexOf('?', firstQuestionMarkIndex + 1);

    // Nếu không tìm thấy ký tự "?" thứ hai, trả về chuỗi gốc
    if (secondQuestionMarkIndex === -1) return str;

    // Loại bỏ ký tự "?" thứ hai
    return str.slice(0, secondQuestionMarkIndex) + str.slice(secondQuestionMarkIndex + 1);
  }

  getCustom<K>(params: Record<string, any> = {}) {
    let strParam = '';
    // Xử lý các tham số bổ sung
    if (Object.keys(params).length > 0) {
      const queryChar = strParam.includes('?') ? '&' : '?';
      const additionalQueryParams = Object.entries(params)
        .map(([key, value]) => `${key}=${String(value)}`)
        .join('&');
      strParam += `${queryChar}${additionalQueryParams}`;
    }

    const urlOrigin = this.removeSecondQuestionMark(`${this.endpoint}${strParam}`);
    return GetMappingAuthorization<K>(urlOrigin, {});
  }

  getAll<T>(
    param: Partial<QueryOptions<T>> = {},
    additionalParams: Record<string, any> = {},
    conf?: AxiosRequestConfig,
  ) {
    let strParam = '';
    if (param) {
      strParam = buildQuery(param as Partial<QueryOptions<T>>);
      // Kiểm tra và thay thế '$search' thành 'search' nếu tồn tại
      if (strParam.includes('$search')) {
        strParam = strParam.replace(/\$search/g, 'search');
      }
    }
    // Xử lý các tham số bổ sung
    if (Object.keys(additionalParams).length > 0) {
      const queryChar = strParam.includes('?') ? '&' : '?';
      const additionalQueryParams = Object.entries(additionalParams)
        .map(([key, value]) => `${key}=${String(value)}`)
        .join('&');
      strParam.replace('?', '');
      strParam = `?${additionalQueryParams}${queryChar}${strParam}`;
    }
    const urlOrigin = this.removeSecondQuestionMark(`${this.endpoint}${strParam}`);
    return GetMappingAuthorization<T[]>(urlOrigin, conf || {});
  }

  getById<T>(id: TKey, param?: Partial<QueryOptions<T>>) {
    let strParam = '';
    if (param) {
      strParam = buildQuery(param as Partial<QueryOptions<T>>);
      // Kiểm tra và thay thế '$search' thành 'search' nếu tồn tại
      if (strParam.includes('$search')) {
        strParam = strParam.replace(/\$search/g, 'search');
      }
    }
    return GetMappingAuthorization<T>(`${this.endpoint}(${id})${strParam}`, {});
  }

  create<T>(data: any) {
    return PostMappingAuthorization<T>(`${this.endpoint}`, data, {
      config: {},
      logout: () => { },
    });
  }

  update<T>(id: TKey, data: any) {
    return PatchMappingAuthorization<T>(`${this.endpoint}(${id})`, data);
  }

  put<T>(id: TKey, data: any) {
    const url = id ? `${this.endpoint}(${id})` : `${this.endpoint}`;
    return PutMappingAuthorization<T>(url, data);
  }

  delete(id: TKey, additionalParams: Array<string> = []) {
    // Xử lý các tham số bổ sung
    let strKey = '';
    if (additionalParams.length > 0) {
      strKey = additionalParams.join(',');
    }
    const strParam = additionalParams.length > 0 ? `(${strKey})` : `(${id})`;
    return DeleteMappingAuthorization<T>(`${this.endpoint}${strParam}`, {});
  }
  deleteWithBody(body: any) {
    return DeleteMappingAuthorization<T>(`${this.endpoint}`, body);
  }

  deleteNone() {
    return DeleteMappingAuthorization<T>(`${this.endpoint}`, {});
  }

  upload<T>({ url, body, config }: Partial<PropsAxios> = {}) {
    const formData = new FormData();
    Object.keys(body).forEach((f) => {
      formData.append(f, body[f]);
    });
    return PostMappingAuthorization<T>(`${this.endpoint}${url}`, formData, { ...config, logout: () => { } });
  }

  // Phương thức để thay đổi prefix
  setPrefix(prefix: PrefixType) {
    this.prefix = prefix;

    // Cập nhật lại endpoint
    const serverName = Reflect.getMetadata(ODATA_SERVER_NAME, this.entityType);
    const controllerName = Reflect.getMetadata(ODATA_CONTROLLER_NAME, this.entityType);
    this.endpoint = `/${serverName}/${this.prefix}/${controllerName}${this.suffix}`;

    return this;
  }

  // Phương thức để thay đổi suffix
  setSuffix(suffix: string) {
    this.suffix = suffix;

    // Cập nhật lại endpoint
    const serverName = Reflect.getMetadata(ODATA_SERVER_NAME, this.entityType);
    const controllerName = Reflect.getMetadata(ODATA_CONTROLLER_NAME, this.entityType);
    this.endpoint = `/${serverName}/${this.prefix}/${controllerName}${this.suffix}`;

    return this;
  }
}
